import React, { useState, useEffect, useContext } from "react";
import type { Theme as ReactSelectTheme } from "react-select";
import type { Row } from "react-table";

import { isNumber } from "../../lib/Util";
import { translate } from "../IntlProviderWrapper";
import { HighlightedText } from "./HighlightedText";
import type { ITags } from "../../lib/Models";
import type { ReactTableColumn } from "./ReactTable";
import { PERMISSION_NAMES } from "../../lib/Auth";
import Authorization from "../Authorization";
import Tooltip from "../Tooltip";
import type { ILabelValuePair } from "../../lib/Util";

type TagType = "boolean" | "string" | "number" | "enum";
type TagValue = boolean | string | number | undefined;
export type TagUpdateValue = TagValue | ILabelValuePair | ILabelValuePair[];

type EnumTagOption = {
    name: string;
};

export type TagData = {
    name: string;
    title?: string;
    type: TagType;
    comment?: string;
    value?: TagValue;
    values?: EnumTagOption[];
    validate?: (value: TagValue) => boolean;
    options?: EnumTagOption[];
    editable?: boolean;
    default_value?: TagValue;
    category?: string;
    mandatory?: boolean;
};

export type TagsData = {
    title: string;
    comment: string;
    key: string;
    tag: TagData;
    default: TagData;
    category?: string;
};

export type TagsConfig = {
    name: string;
    values: TagData[];
    categories?: { name: string; title: string }[];
};

type onChangeType = {
    onChange: (value: TagValue) => void;
};
type NumberCellState = {
    error?: string | boolean | null;
    value: TagValue;
};

export type AdditionalConfig = {
    [key: string]: Partial<TagData>;
};

type ButtonSetDefaultType = {
    type: string;
    value: TagValue;
    editable?: boolean;
    name: string;
    default_value?: TagValue;
};

export const getInitialState = () => ({
    sortBy: [
        {
            id: "key",
        },
    ],
});

const getPermissions = (value: TagValue, editable?: boolean) => {
    let permissions = PERMISSION_NAMES.Everyone;

    if (editable === false) {
        permissions = PERMISSION_NAMES.AdminOnly;
    }

    return permissions;
};

const formatDefaultValue = (default_value: TagValue, type: string): string => {
    if (default_value === undefined) {
        return "";
    }

    let formatted = String(default_value);

    if (type === "string" || type === "enum") {
        if (default_value === "") {
            formatted = "empty string";
        } else {
            // Wrap non empty strings with quotes
            formatted = `"${formatted}"`;
        }
    }

    return formatted;
};

const ButtonSetDefault = ({ type, onChange, value, editable, default_value }: onChangeType & ButtonSetDefaultType) => {
    const permissions = getPermissions(value, editable);
    const is_default_set = value === undefined;
    const tooltip_default_value = (
        <>
            {translate("common.default")}:{" "}
            <span className="text-monospace">{formatDefaultValue(default_value, type)}</span>
        </>
    );

    if (is_default_set) {
        return null;
    } else {
        return (
            <Tooltip content={tooltip_default_value}>
                <Authorization.button
                    permission={permissions}
                    className="btn btn-outline-danger"
                    onClick={() => onChange(undefined)}
                >
                    {translate("common.set_default", "Set default")}
                </Authorization.button>
            </Tooltip>
        );
    }
};

const BooleanCell = ({ value, onChange, editable }: onChangeType & TagData) => {
    if (!value) {
        value = undefined;
    } else {
        if (typeof value === "string") {
            value = value === "true";
        }
    }

    return (
        <Authorization.Toggle
            checked={value}
            permission={getPermissions(value, editable)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onChange(event.currentTarget.checked);
            }}
        />
    );
};

const NumberCell = (props: onChangeType & TagData) => {
    const { value, onChange, validate, editable } = props;
    const [state, setState] = useState<NumberCellState>({ error: null, value });

    useEffect(() => {
        setState((prevState: NumberCellState) => ({ ...prevState, value }));
    }, [props]);

    const validateNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
        const target_value = e.currentTarget.value;
        const newValue = Number(target_value && /\d/.test(target_value) ? target_value : 0);

        if (value === newValue) return;

        let error: string | boolean | null = null;

        if (!isNumber(newValue)) {
            error = translate("common.error_number", "Invalid number");
        }

        if (validate && !error) {
            error = validate(newValue);
        }

        if (error) {
            return setState((prevState: NumberCellState) => ({ ...prevState, error }));
        }

        setState({ error: null, value: newValue });

        return onChange(newValue);
    };

    const validateIntermediateNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
        const new_value = e.currentTarget.value;
        const number_reg_ex = /^-?\d*\.?\d*$/;
        let error = null;

        if (!number_reg_ex.test(e.currentTarget.value)) {
            error = translate("common.error_number", "Invalid number");
        }

        if (error) {
            return setState((prev_state: NumberCellState) => ({ ...prev_state, error }));
        }

        setState({ error: null, value: new_value });

        return onChange(new_value);
    };

    return (
        <>
            {state.error ? <span> {state.error} </span> : null}
            <Authorization.input
                permission={getPermissions(value, editable)}
                type="text"
                className="form-control form-control-sm text-right"
                value={state.value}
                title={state.value}
                onChange={validateIntermediateNumber}
                onBlur={validateNumber}
            />
        </>
    );
};

type StringCellState = {
    value: TagValue;
    error: string | boolean | undefined;
};

const StringCell = (props: onChangeType & TagData) => {
    const { onChange, validate, editable, value } = props;
    const [state, setState] = useState<StringCellState>({ value, error: undefined });

    useEffect(() => {
        if (state.value !== value) {
            setState((prevState: StringCellState) => ({ ...prevState, value }));
        }
    }, [value]);

    const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.currentTarget.value;

        if (validate) {
            const error = validate(newValue);
            if (error) {
                setState((prevState: StringCellState) => ({ ...prevState, error }));
                return;
            }
        }

        onChange(newValue);
        setState((prevState: StringCellState) => ({ ...prevState, value: newValue }));
    };

    return (
        <Authorization.input
            permission={getPermissions(value, editable)}
            type="text"
            className="form-control form-control-sm"
            value={state.value}
            title={state.value}
            onChange={onInputChange}
        />
    );
};

type ReactTableTagsCellType = {
    cell: {
        value: TagData;
    };
    onChangeCell: (name: string) => (value: TagValue) => void;
};

type SelectCellProps = {
    value: string | null;
    name: string;
    onChange: (value: TagValue) => void;
    validate?: (value: TagValue) => boolean;
    options: EnumTagOption[];
    editable?: boolean;
};

const SelectCell = (props: SelectCellProps) => {
    const { value, name, editable } = props;

    const options = props.options.map(v => ({ label: v.name, value: v.name }));
    let option: ILabelValuePair | null = null;

    if (value) {
        option = { label: value, value };
    }

    return (
        <Authorization.Select
            className="react-select react-select--sm"
            classNamePrefix="react-select"
            theme={(theme: ReactSelectTheme) => ({
                ...theme,
                spacing: {
                    ...theme.spacing,
                    baseUnit: 2,
                    controlHeight: 30,
                },
            })}
            permission={getPermissions(value ?? undefined, editable)}
            name={name}
            value={option}
            title={option}
            options={options}
            onChange={props.onChange}
        />
    );
};

export const ReactTableButtonDefaultCell = (props: ReactTableTagsCellType) => {
    const { type, value, name, editable, default_value }: TagData = props.cell.value;

    return (
        default_value !== undefined && (
            <ButtonSetDefault
                onChange={props.onChangeCell(name)}
                type={type}
                value={value}
                editable={editable}
                name={name}
                default_value={default_value}
            />
        )
    );
};

export const ReactTableTagsCell = (props: ReactTableTagsCellType) => {
    const { type, value, name, validate, options, editable, default_value }: TagData = props.cell.value;

    let component: React.ReactNode = null;

    if (type === "boolean") {
        const displayValue = value ?? default_value ?? false;

        component = (
            <BooleanCell
                type={type}
                value={displayValue}
                onChange={props.onChangeCell(name)}
                name={name}
                validate={validate}
                editable={editable}
            />
        );
    }

    if (type === "number") {
        const displayValue = value ?? default_value ?? 0;
        component = (
            <NumberCell
                type={type}
                value={displayValue}
                onChange={props.onChangeCell(name)}
                name={name}
                validate={validate}
                editable={editable}
            />
        );
    }

    if (type === "string") {
        const displayValue = value ?? default_value ?? "";
        component = (
            <StringCell
                type={type}
                value={displayValue}
                onChange={props.onChangeCell(name)}
                name={name}
                validate={validate}
                editable={editable}
            />
        );
    }

    if (type === "enum") {
        const displayValue = value ?? default_value ?? options?.[0]?.name ?? "";
        component = (
            <SelectCell
                name={name}
                value={String(displayValue)}
                options={options || []}
                onChange={props.onChangeCell(name)}
                editable={editable}
            />
        );
    }

    return component;
};

export const updateTagsConfig = (tags_config: TagsConfig, additional_config?: AdditionalConfig): TagsConfig => {
    tags_config.values = tags_config.values.map(el => {
        if (additional_config && additional_config[el.name]) {
            return {
                ...el,
                ...additional_config[el.name],
            };
        }

        return el;
    });

    return tags_config;
};

export const rowStyle = (row: Row<TagsData>): React.CSSProperties => {
    let style: React.CSSProperties = {};

    let mandatoryCell = row.cells.filter(cell => cell.column.id === "mandatory");

    if (mandatoryCell.length > 0) {
        mandatoryCell = mandatoryCell[0];

        if (mandatoryCell.column.id === "mandatory") {
            style.backgroundColor = "white";
        }

        if (
            mandatoryCell.value.value !== true &&
            mandatoryCell.row.values.tag &&
            mandatoryCell.row.values.tag.value === undefined
        ) {
            style.backgroundColor = "#eaeaea";
        }
    }

    return style;
};

/** Returns "value-set" for rows where the tag has an explicit value (not using default). */
export const rowClassName = (row: Row<TagsData>): string => {
    const tag: TagData = row.original?.tag;
    if (tag && tag.value !== undefined) return "tags-row-value-set";
    return "";
};

export const TagsTableSearchContext = React.createContext<string | null>(null);

const ReactTableTagsInfoCell = ({ row }: { row: { original: TagsData } }) => {
    const {
        title,
        key,
        comment,
        tag: { type },
    } = row.original;
    const searchQuery = useContext(TagsTableSearchContext);
    const highlight = searchQuery && searchQuery.trim();
    return (
        <>
            <div className="text-monospace mb-1 small text-muted">
                {highlight ? <HighlightedText text={key ?? ""} query={highlight ?? ""} /> : key}{" "}
                <span className="text-extra-muted">{type}</span>
            </div>
            <h5 className="mb-1">
                {highlight ? <HighlightedText text={title ?? ""} query={highlight ?? ""} /> : title}
            </h5>
            {comment ? (
                <p className="mr-5 mb-0">
                    {highlight ? <HighlightedText text={comment} query={highlight ?? ""} /> : comment}
                </p>
            ) : null}
        </>
    );
};

export const Columns: ReactTableColumn<TagsData>[] = [
    {
        Header: "",
        accessor: "key",
        id: "info",
        disableFilters: true,
        Cell: ReactTableTagsInfoCell,
        width: "75%",
    },
    {
        Header: "",
        accessor: "default",
        disableFilters: true,
        Cell: ReactTableButtonDefaultCell,
    },
    {
        Header: "",
        accessor: "tag",
        disableFilters: true,
        Cell: ReactTableTagsCell,
    },
];

type TagsAccess = Record<string, (tags: ITags) => TagValue>;
type TagCategory = { name: string; title: string; rows: TagsData[] };

/** Map tags to rows and group by category. If no categories, returns a single group with all rows. */
export const mapDataToCategories = (
    tags_data: ITags,
    tags_config: TagsConfig,
    tags_access: TagsAccess = {}
): TagCategory[] => {
    const allRows = mapDataToTags(tags_data, tags_config, tags_access);
    const hasCategories = tags_config.categories && tags_config.categories.length > 0;

    if (!hasCategories) {
        return [{ name: "", title: "", rows: allRows }];
    }

    const categories = tags_config.categories || [];
    return categories.map(category => ({
        name: category.name,
        title: category.title,
        rows: allRows.filter(row => row.category === category.name).sort((a, b) => b.key.localeCompare(a.key)),
    }));
};

const mapDataToTags = (tags_data: ITags, tags_config: TagsConfig, tags_access: TagsAccess = {}): TagsData[] => {
    const data: TagsData[] = tags_config.values.map(tag => {
        const value = tags_data[tag.name];
        const default_value = typeof tags_access[tag.name] === "function" ? tags_access[tag.name]({}) : undefined;

        const group_name = tags_config.name;
        const comment = translate(`tag_groups.${group_name}.values.comment.${tag.name}`, `Missing`);
        const title = translate(`tag_groups.${group_name}.values.title.${tag.name}`, `Missing`);

        const tagObj: TagData = {
            name: tag.name,
            type: tag.type,
            value,
            validate: tag.validate,
            options: tag.values,
            editable: tag.editable,
            default_value: default_value,
        };

        return {
            title: title,
            comment: comment,
            key: tag.name,
            tag: tagObj,
            default: tagObj,
            category: tag.category,
        };
    });

    return data;
};

/** Given configuration of known tags, collect the configuration for the unknown ones and
 * treat them as strings.
 */
export const mapDataToCustomTags = (tags_data: ITags, tags_config: TagsConfig): TagsData[] => {
    const known_tags = tags_config.values.map(x => x.name);
    const data: TagsData[] = [];
    for (const tag_name of Object.keys(tags_data)) {
        if (known_tags.indexOf(tag_name) >= 0) {
            // This is a known tag, just skip it
            continue;
        }
        const comment = "";
        const title = tag_name.replace(/_/gi, " ");
        const tagObj: TagData = {
            name: tag_name,
            type: "string",
            value: tags_data[tag_name],
            editable: true,
        };

        data.push({
            comment: comment,
            title: title,
            key: tag_name,
            tag: tagObj,
            default: tagObj,
        });
    }
    return data;
};

export const onChangeUpdateTags = (tags: ITags, name: string, value: TagUpdateValue): ITags => {
    const new_tags: ITags = { ...tags };

    if (value === undefined) {
        // This deletes a custom tag competely when setDefault is clicked.
        // It's currently prevented because setDefault button is not shown
        // for tags that don't have a default value (like custom tags).
        delete new_tags[name];
    } else if (typeof value === "object") {
        // Select option
        if (Array.isArray(value) && value.length > 0) {
            new_tags[name] = value[0].value;
        } else if ("value" in value) {
            new_tags[name] = value.value;
        }
    } else {
        new_tags[name] = String(value);
    }

    return new_tags;
};
